'use client'

import { Tooltip } from 'shared/components/tooltip'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { twJoin } from 'tailwind-merge'

interface RelativeTimeProps {
  time: string
}

const SECOND = 1_000
const MINUTE = 60 * SECOND
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR
const WEEK = 7 * DAY
const MONTH = 30 * DAY
const YEAR = 365 * DAY

type RelativeTimeParts =
  | { future: true; formattedDateTime: string }
  | {
      future: false
      relativeTime: string
      formattedDateTime: string
      formattedDate: string
      formattedTime: string
    }

function formatShortDateTime(date: Date, intlLocale: string, timeZone?: string) {
  return new Intl.DateTimeFormat(intlLocale, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  }).format(date)
}

function useRelativeTimeParts(time: string): RelativeTimeParts | null {
  const { t, tICU } = useLocoTranslation()
  const { intlLocale, user } = useUser()
  const date = new Date(time)
  const elapsed = Date.now() - date.getTime()

  if (!user || Number.isNaN(date.getTime())) return null

  const formattedDateTime = formatShortDateTime(date, intlLocale, user.timezone)

  if (elapsed < 0) return { future: true, formattedDateTime }

  let relativeTime: string

  if (elapsed < MINUTE) {
    relativeTime = t('global.times.just_now')
  } else if (elapsed < HOUR) {
    relativeTime = tICU('global.times.minutes_ago', { count: Math.floor(elapsed / MINUTE) })
  } else if (elapsed < DAY) {
    relativeTime = tICU('global.times.hours_ago', { count: Math.floor(elapsed / HOUR) })
  } else if (elapsed < WEEK) {
    relativeTime = tICU('global.times.days_ago', { count: Math.floor(elapsed / DAY) })
  } else if (elapsed < MONTH) {
    relativeTime = tICU('global.times.weeks_ago', { count: Math.floor(elapsed / WEEK) })
  } else if (elapsed < YEAR) {
    relativeTime = tICU('global.times.months_ago', { count: Math.floor(elapsed / MONTH) })
  } else {
    relativeTime = tICU('global.times.years_ago', { count: Math.floor(elapsed / YEAR) })
  }

  return {
    future: false,
    relativeTime,
    formattedDateTime,
    formattedDate: new Intl.DateTimeFormat(intlLocale, {
      dateStyle: 'long',
      timeZone: user.timezone,
    }).format(date),
    formattedTime: new Intl.DateTimeFormat(intlLocale, {
      timeStyle: 'short',
      timeZone: user.timezone,
    }).format(date),
  }
}

interface CompactTimeViewProps {
  time: string
  relativeTime: string
  formattedDateTime: string
  /** Stacks the two parts from tablet up, where a table column is too narrow for one line. */
  stackFromTablet?: boolean
}

function CompactTimeView({
  time,
  relativeTime,
  formattedDateTime,
  stackFromTablet = false,
}: CompactTimeViewProps) {
  return (
    <span
      className={twJoin(
        'flex flex-wrap items-center gap-1 text-sm',
        stackFromTablet && 'desktop:hidden tablet:flex-col tablet:items-start',
      )}
    >
      <time className={'text-text-tertiary'} dateTime={time}>
        {relativeTime}
      </time>
      <span className={twJoin('text-icon-tertiary', stackFromTablet && 'tablet:hidden')}>•</span>
      <time className={'text-text-quaternary'} dateTime={time}>
        {formattedDateTime}
      </time>
    </span>
  )
}

interface TooltipTimeProps {
  time: string
  relativeTime: string
  formattedDate: string
  formattedTime: string
}

/*
 * The trigger is inline, so the time stays on the line of the text around it and a truncating
 * parent ends it with an ellipsis. A block trigger broke the line before and after the time and was
 * clipped mid-letter instead.
 */
function TooltipTime({ time, relativeTime, formattedDate, formattedTime }: TooltipTimeProps) {
  return (
    <span className="max-desktop:hidden contents">
      <Tooltip
        label={
          <span className="text-sm">
            {formattedDate} <span className="text-text-tertiary">•</span> {formattedTime}
          </span>
        }
        triggerProps={{ render: <span /> }}
      >
        <time dateTime={time}>{relativeTime}</time>
      </Tooltip>
    </span>
  )
}

/** Relative and absolute time on one line at every breakpoint, for rows with room for both. */
function CompactTime({ time }: RelativeTimeProps) {
  const parts = useRelativeTimeParts(time)

  if (!parts) return null
  if (parts.future) return <time dateTime={time}>{parts.formattedDateTime}</time>

  return (
    <CompactTimeView
      time={time}
      relativeTime={parts.relativeTime}
      formattedDateTime={parts.formattedDateTime}
    />
  )
}

function RelativeTime({ time }: RelativeTimeProps) {
  const parts = useRelativeTimeParts(time)

  if (!parts) return null
  if (parts.future) return <time dateTime={time}>{parts.formattedDateTime}</time>

  // Both forms render and CSS picks one: the first-paint breakpoint comes from the
  // request User-Agent, so choosing here would repaint once hydration corrects it.
  return (
    <>
      <CompactTimeView
        time={time}
        relativeTime={parts.relativeTime}
        formattedDateTime={parts.formattedDateTime}
        stackFromTablet
      />
      <TooltipTime
        time={time}
        relativeTime={parts.relativeTime}
        formattedDate={parts.formattedDate}
        formattedTime={parts.formattedTime}
      />
    </>
  )
}

export { CompactTime, RelativeTime }
export type { RelativeTimeProps }
